/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useState } from "react";
import { DOC_ERROR_SEVERITY } from "@adapptio/lib-blueprint";

import { Layout } from "../../../../ui/components/Layout/Layout";
import { TViewComponent } from "../../../../view/IView";
import { ActionEditorView } from "../ActionEditorView";
import { ActionEditorOutlineContent, ActionEditorOutlineToolbar } from "./ActionEditorOutline";
import { ActionEditorDataExplorerContent, ActionEditorDataExplorerToolbar } from "./ActionEditorDataExplorer";
import { ActionEditorMainToolbar } from "./ActionEditorMainToolbar";
import { ActionEditorConsoleContent } from "./ActionEditorConsole";
import { ActionEditorInspector } from "./ActionEditorInspector";
import { ActionEditorSettings } from "./ActionEditorSettings";
import { ActionEditorNodePalette } from "./ActionEditorNodePalette";
import { ActionEditorTesterContent } from "./ActionEditorTester";
import { ActionEditorMainContent } from "./ActionEditorMainContent";
import { ActionEditorSecondaryToolbar } from "./ActionEditorSecondaryToolbar";
import { ActionEditorConsoleFilter } from "./ActionEditorConsoleFilters";
import { ActionEditorTesterButton } from "./ActionEditorTesterButton";
import { ConsoleCountBadge } from "../../../../ui/components/ConsoleCountBadge";
import { ReferenceListPanel } from "../../../../ui/components/ReferenceList/ReferenceListPanel";
import { ReferenceCountBadge } from "../../../../ui/components/ReferenceList/ReferenceCountBadge";

export const ActionEditorEditMain: TViewComponent<ActionEditorView> = ({ view }) => {
	const [ severityFilter, setSeverityFilter ] = useState<Set<DOC_ERROR_SEVERITY>>(new Set());
	const actionModel = useMemo(() => view.getActionModel(), [ view ]);
	const layoutState = useMemo(() => view.getLayout(), []);

	const handleFilterSelect = useCallback<(severity: DOC_ERROR_SEVERITY) => void>((severity) => {
		setSeverityFilter((prevState) => {
			prevState.delete(severity) || prevState.add(severity);
			return new Set(prevState);
		});
	}, []);

	const renderComponent = (componentName: string) => {
		switch (componentName) {
			case "outlineToolbar": {
				return <ActionEditorOutlineToolbar view={view} />;
			}
			case "outlineContent": {
				return <ActionEditorOutlineContent view={view} />;
			}

			case "dataExplorerToolbar": {
				return <ActionEditorDataExplorerToolbar />;
			}
			case "dataExplorerContent": {
				return <ActionEditorDataExplorerContent model={actionModel} view={view} />;
			}

			case "mainToolbar": {
				return <ActionEditorMainToolbar view={view} />;
			}
			case "mainContent": {
				return <ActionEditorMainContent view={view} />;
			}
			case "secondaryMainToolbar": {
				return <ActionEditorSecondaryToolbar view={view} />;
			}

			case "tester": {
				return <ActionEditorTesterContent view={view} />;
			}

			case "testerRunner": {
				return <ActionEditorTesterButton view={view} />;
			}

			case "console": {
				return <ActionEditorConsoleContent model={actionModel} severityFilter={severityFilter} />;
			}

			case "consoleCount": {
				return <ConsoleCountBadge model={actionModel} />;
			}

			case "consoleFilters": {
				return (
					<ActionEditorConsoleFilter
						model={actionModel}
						severityFilter={severityFilter}
						onFilterSelect={handleFilterSelect}
					/>
				);
			}

			case "references": {
				return <ReferenceListPanel
					resourceId={view.getResource().id}
					dCtx={actionModel.getDesignContext()}
					resourceService={view.getResourceService()}
				/>;
			}

			case "referencesCount": {
				return <ReferenceCountBadge
					resourceId={view.getResource().id}
					dCtx={actionModel.getDesignContext()}
					resourceService={view.getResourceService()}
				/>;
			}

			case "nodePalette": {
				return <ActionEditorNodePalette view={view} />;
			}
			case "inspector": {
				return <ActionEditorInspector view={view} />;
			}
			case "settings": {
				return <ActionEditorSettings model={actionModel} />;
			}

			default: {
				return <p>Error: Undefined panel {componentName}.</p>;
			}
		}
	};

	return <Layout state={layoutState} renderComponent={renderComponent} />;
};
